import React, { useEffect, useState } from "react";
import AppHeader from "../../../components/Header/AppHeader";
import { apiWrapWithErrorWithData } from "../../../utilities/apiHelpers";
import {
  ActionIcon,
  Button,
  Modal,
  MultiSelect,
  Select,
  Table,
  TextInput,
  Card,
  Badge,
  Grid,
  Group,
  Text,
  Title,
  Tooltip,
  Divider,
  LoadingOverlay,
} from "@mantine/core";
import {
  IconEdit,
  IconEye,
  IconEyeOff,
  IconTrash,
  IconSearch,
  IconPlus,
  IconX,
  IconFilter,
  IconUsers,
  IconBuilding,
} from "@tabler/icons-react";
import DeleteConfirmationModal from "../../../components/DeleteConfirmationModal";
import { useNavigate } from "react-router-dom";
import {
  addMasterUser,
  deleteMasterUser,
  getBuList,
  getUserFromMaster,
  getUserRoleList,
  updateMasterUser,
} from "../../../utilities/apis/users";
import { validateEmail } from "../../../utilities/utilities";
import { showNotification } from "@mantine/notifications";
import { BeatLoader } from "react-spinners";
import colors from "../../../utilities/design";

// Module name mapping helper
const MODULE_IDS = {
  agreement: 1,
  recovery: 2,
  obligation: 3,
  document: 4,
};

const getModuleId = (name) => MODULE_IDS[name?.toLowerCase()] || null;

const deptRequiredUserRoles = [17, 18, 19];

// Initial form state definition
const initialFormState = {
  name: "",
  email: "",
  phone: "1212121212",
  password: "",
  userRole: [], // array of string IDs
  mapOrganization: "1",
  mapBusinessunit: "1",
  hasAccessTo: [], // array of string IDs
  enabled: "true", // "true" or "false" string
  department: "",
};

const TeamManager = () => {
  const navigate = useNavigate();

  // Core Page State
  const [userData, setUserData] = useState({
    loading: true,
    users: [],
  });
  const [buData, setBuData] = useState([]);
  const [userRoleData, setUserRoleData] = useState([]);
  const [roleGroups, setRoleGroups] = useState(null);

  // Search & Filter State
  const [searchTerm, setSearchTerm] = useState("");
  const [filterBu, setFilterBu] = useState("all");
  const [filterModule, setFilterModule] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  // Modal & Form State
  const [editId, setEditId] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formState, setFormState] = useState(initialFormState);
  const [formErrors, setFormErrors] = useState({});
  const [formLoading, setFormLoading] = useState(false);

  // Delete Confirmation State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Fetch roles list
  const fetchUserRoleList = async () => {
    const response = await apiWrapWithErrorWithData(getUserRoleList());

    if (response.success) {
      const formattedData = response.result.map((userRole) => ({
        value: userRole.id.toString(),
        label: userRole.moduleName + " => " + userRole.name,
        moduleId: String(getModuleId(userRole.moduleName) || ""),
      }));
      setUserRoleData(formattedData);

      const roleMapping = response.result.reduce((acc, { id, moduleName }) => {
        const moduleId = getModuleId(moduleName);
        if (moduleId) {
          acc[moduleId] = acc[moduleId] || [];
          acc[moduleId].push(id);
        }
        return acc;
      }, {});
      setRoleGroups(roleMapping);
    }
  };

  // Fetch business units
  const bulist = async () => {
    const response = await apiWrapWithErrorWithData(getBuList());

    if (response.success) {
      const formattedData = response.result.map(({ id, name }) => ({
        value: id.toString(),
        label: name,
      }));
      setBuData(formattedData);
    }
  };

  // Fetch users
  const fetchUsers = async () => {
    setUserData((prev) => ({ ...prev, loading: true }));
    const response = await apiWrapWithErrorWithData(getUserFromMaster());
    if (response.success) {
      setUserData({
        loading: false,
        users: response.result.userList || [],
      });
    } else {
      setUserData({
        loading: false,
        users: [],
      });
    }
  };

  useEffect(() => {
    bulist();
    fetchUserRoleList();
    fetchUsers();
  }, []);

  // Delete User Handler
  const deleteUserHandler = async () => {
    if (!userToDelete) return;

    setDeleteLoading(true);
    const response = await apiWrapWithErrorWithData(
      deleteMasterUser({ id: userToDelete.id }),
    );

    if (response.success) {
      showNotification({
        message: "User has been deleted",
        color: "green",
        title: "Delete user",
      });
      setDeleteModalOpen(false);
      setUserToDelete(null);
      await fetchUsers();
    } else {
      showNotification({
        message: "Error: User could not be deleted",
        color: "red",
        title: "Error deleting user",
      });
    }
    setDeleteLoading(false);
  };

  // Helper validation for modules vs roles
  const isValidUserRoles = (allowedModules, userRoles) => {
    if (!roleGroups) return false;

    return allowedModules.every((module) => {
      const moduleId = Number(module);
      const moduleRoles = roleGroups[moduleId];
      if (!moduleRoles?.length) return false;

      return userRoles.some((role) => moduleRoles.includes(Number(role)));
    });
  };

  // Form Validation
  const validate = () => {
    const nextErrors = {};
    const isEdit = editId > 0;

    if (!formState.name?.trim()) {
      nextErrors.name = "Please enter the name";
    }

    const email = formState.email?.trim() ?? "";
    if (!email) {
      nextErrors.email = "Please enter email";
    } else if (!validateEmail(email)) {
      nextErrors.email = "Please enter a valid email";
    }

    if (!isEdit && (formState.password?.length ?? 0) < 5) {
      nextErrors.password = "Please enter minimum 5 characters";
    }

    if (!formState.mapBusinessunit) {
      nextErrors.mapBusinessunit = "Please select business unit";
    }

    if (!formState.hasAccessTo?.length) {
      nextErrors.hasAccessTo = "Please select allowed module/s";
    }

    if (!formState.userRole?.length) {
      nextErrors.userRole = "Please select user roles";
    } else if (
      formState.hasAccessTo?.length &&
      !isValidUserRoles(formState.hasAccessTo, formState.userRole)
    ) {
      nextErrors.userRole =
        "Please select valid user roles for the selected module/s";
    }

    const needsDepartment =
      !isEdit &&
      formState.hasAccessTo?.some((module) => Number(module) === 3) &&
      formState.userRole?.some((id) =>
        deptRequiredUserRoles.includes(Number(id)),
      );

    if (needsDepartment && !formState.department) {
      nextErrors.department = "Please select user department";
    }

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  // Set form state for update (Edit User)
  const setUpdateUserData = (user) => {
    setFormState({
      name: user.name || "",
      email: user.email || "",
      phone: user.phone || "1212121212",
      password: "",
      userRole: user.userRoleIds
        ? user.userRoleIds.split(", ").map((id) => id.trim())
        : [],
      mapOrganization: String(user.mapOrganization || "1"),
      mapBusinessunit: String(user.mapBusinessunit || "1"),
      hasAccessTo: user.userModuleIds
        ? user.userModuleIds.split(", ").map((id) => id.trim())
        : [],
      enabled: user.enabled ? "true" : "false",
      department: "",
    });
    setEditId(user.id);
    setModalOpen(true);
  };

  // Close and reset modal/form
  const handleCloseModal = () => {
    setShowPassword(false);
    setEditId(0);
    setFormState(initialFormState);
    setFormErrors({});
    setModalOpen(false);
  };

  // Filtered users calculation
  const filteredUsers = userData.users.filter((user) => {
    const matchesSearch =
      (user.name?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
      (user.email?.toLowerCase() || "").includes(searchTerm.toLowerCase());

    const matchesBu =
      filterBu === "all" || String(user.mapBusinessunit) === filterBu;

    const matchesModule =
      filterModule === "all" ||
      (user.userModule?.toLowerCase() || "").includes(
        filterModule.toLowerCase(),
      );

    const matchesStatus =
      filterStatus === "all" ||
      (filterStatus === "active" && user.enabled) ||
      (filterStatus === "inactive" && !user.enabled);

    return matchesSearch && matchesBu && matchesModule && matchesStatus;
  });

  // Avatar helper functions
  const getAvatarColor = (name) => {
    if (!name) return "bg-blue-500";
    const colorsList = [
      "bg-indigo-500",
      "bg-blue-500",
      "bg-teal-500",
      "bg-green-500",
      "bg-orange-500",
      "bg-pink-500",
      "bg-purple-500",
      "bg-cyan-500",
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colorsList.length;
    return colorsList[index];
  };

  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:p-4">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-950">
              Team Management
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage user roles, module permissions, departments, and business
              units.
            </p>
          </div>
          <Button
            color="blue"
            onClick={() => {
              setEditId(0);
              setModalOpen(true);
            }}
            leftIcon={<IconPlus size={16} />}
          >
            Add User
          </Button>
        </div>

        {/* Search & Filter Bar */}
        <Card
          shadow="sm"
          radius="md"
          p="md"
          withBorder
          className="bg-white mb-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div>
              <TextInput
                label="Search member"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                icon={<IconSearch size={16} className="text-gray-400" />}
                rightSection={
                  searchTerm && (
                    <ActionIcon
                      onClick={() => setSearchTerm("")}
                      size="sm"
                      radius="xl"
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  )
                }
              />
            </div>
            <div>
              <Select
                label="Filter by Business Unit"
                placeholder="All Business Units"
                value={filterBu}
                onChange={setFilterBu}
                withinPortal
                data={[
                  { value: "all", label: "All Business Units" },
                  ...buData,
                ]}
              />
            </div>
            <div>
              <Select
                label="Filter by Module"
                placeholder="All Modules"
                value={filterModule}
                onChange={setFilterModule}
                withinPortal
                data={[
                  { value: "all", label: "All Modules" },
                  { value: "agreement", label: "Agreement" },
                  { value: "recovery", label: "Recovery" },
                  { value: "obligation", label: "Obligation" },
                ]}
              />
            </div>
            <div className="flex gap-2">
              <div className="flex-1">
                <Select
                  label="Filter by Status"
                  placeholder="All Statuses"
                  value={filterStatus}
                  onChange={setFilterStatus}
                  withinPortal
                  data={[
                    { value: "all", label: "All Statuses" },
                    { value: "active", label: "Active" },
                    { value: "inactive", label: "Inactive" },
                  ]}
                />
              </div>
              {(searchTerm ||
                filterBu !== "all" ||
                filterModule !== "all" ||
                filterStatus !== "all") && (
                <Tooltip label="Reset Filters">
                  <ActionIcon
                    color="gray"
                    variant="light"
                    size="lg"
                    onClick={() => {
                      setSearchTerm("");
                      setFilterBu("all");
                      setFilterModule("all");
                      setFilterStatus("all");
                    }}
                    className="mb-0.5"
                  >
                    <IconFilter size={18} />
                  </ActionIcon>
                </Tooltip>
              )}
            </div>
          </div>
        </Card>

        {/* Table Container Card */}
        <Card
          shadow="sm"
          radius="md"
          p={0}
          withBorder
          className="bg-white overflow-hidden"
        >
          {userData.loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <BeatLoader color={colors.primary} size={12} />
              <Text size="sm" color="dimmed">
                Loading team members...
              </Text>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
              <div className="p-4 bg-gray-50 rounded-full text-gray-400 mb-3">
                <IconSearch size={32} />
              </div>
              <Text size="md" className="font-semibold text-gray-900">
                No members found
              </Text>
              <Text size="sm" color="dimmed" className="max-w-sm mt-1">
                Try adjusting your search terms or filters to find what you're
                looking for.
              </Text>
              {(searchTerm ||
                filterBu !== "all" ||
                filterModule !== "all" ||
                filterStatus !== "all") && (
                <Button
                  size="xs"
                  variant="subtle"
                  color="blue"
                  onClick={() => {
                    setSearchTerm("");
                    setFilterBu("all");
                    setFilterModule("all");
                    setFilterStatus("all");
                  }}
                  className="mt-4"
                >
                  Clear all filters
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table
                verticalSpacing="sm"
                horizontalSpacing="md"
                className="min-w-full divide-y divide-gray-100"
              >
                <thead className="bg-gray-50/70">
                  <tr>
                    <th className="text-left py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="text-left py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Member
                    </th>
                    <th className="text-left py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Modules
                    </th>
                    <th className="text-left py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Roles
                    </th>
                    <th className="text-left py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Business Unit
                    </th>
                    <th className="text-left py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="py-3.5">&nbsp;</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {filteredUsers.map((user, index) => {
                    const initials = getInitials(user.name);
                    const avatarBg = getAvatarColor(user.name);

                    return (
                      <tr
                        key={user.id}
                        className="hover:bg-gray-50/50 transition-colors"
                      >
                        <td className="text-sm text-gray-500">{index + 1}</td>
                        <td>
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-9 h-9 rounded-full ${avatarBg} text-white flex items-center justify-center text-sm font-semibold tracking-wide shadow-sm`}
                            >
                              {initials}
                            </div>
                            <div>
                              <Text
                                size="sm"
                                className="font-semibold text-gray-900 leading-none"
                              >
                                {user.name}
                              </Text>
                              <Text
                                size="xs"
                                color="dimmed"
                                className="mt-1 leading-none"
                              >
                                {user.email}
                              </Text>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="flex flex-wrap gap-1">
                            {user.userModule ? (
                              user.userModule.split(", ").map((mod) => {
                                const lowerMod = mod.trim().toLowerCase();
                                let badgeColor = "gray";
                                if (lowerMod.includes("agreement"))
                                  badgeColor = "cyan";
                                else if (lowerMod.includes("recovery"))
                                  badgeColor = "green";
                                else if (lowerMod.includes("obligation"))
                                  badgeColor = "orange";
                                else if (lowerMod.includes("document"))
                                  badgeColor = "grape";
                                return (
                                  <Badge
                                    key={mod}
                                    color={badgeColor}
                                    variant="light"
                                    size="sm"
                                  >
                                    {mod.trim()}
                                  </Badge>
                                );
                              })
                            ) : (
                              <Text size="xs" color="dimmed">
                                —
                              </Text>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="flex flex-wrap gap-1 max-w-xs">
                            {user.userRole ? (
                              user.userRole.split(", ").map((role) => (
                                <Badge
                                  key={role}
                                  color="indigo"
                                  variant="outline"
                                  size="xs"
                                >
                                  {role.trim()}
                                </Badge>
                              ))
                            ) : (
                              <Text size="xs" color="dimmed">
                                —
                              </Text>
                            )}
                          </div>
                        </td>
                        <td>
                          <Text
                            size="sm"
                            className="text-gray-700 flex items-center gap-1.5"
                          >
                            <IconBuilding size={15} className="text-gray-400" />
                            {user.userBusinessunit || "—"}
                          </Text>
                        </td>
                        <td>
                          {user.enabled ? (
                            <Badge
                              color="green"
                              variant="filled"
                              size="sm"
                              className="px-2 font-semibold tracking-wide"
                            >
                              Active
                            </Badge>
                          ) : (
                            <Badge
                              color="gray"
                              variant="filled"
                              size="sm"
                              className="px-2 font-semibold tracking-wide"
                            >
                              Inactive
                            </Badge>
                          )}
                        </td>
                        <td>
                          <div className="flex justify-end gap-1.5 pr-2">
                            <Tooltip
                              label="Edit Member"
                              withArrow
                              position="left"
                            >
                              <ActionIcon
                                color="blue"
                                variant="subtle"
                                onClick={() => {
                                  setUpdateUserData(user);
                                }}
                              >
                                <IconEdit size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip
                              label="Delete Member"
                              withArrow
                              position="left"
                            >
                              <ActionIcon
                                color="red"
                                variant="subtle"
                                onClick={() => {
                                  setUserToDelete(user);
                                  setDeleteModalOpen(true);
                                }}
                              >
                                <IconTrash size={18} />
                              </ActionIcon>
                            </Tooltip>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card>
      </div>

      <Modal
        title={
          <Group spacing="xs">
            <div
              className={`p-2 rounded-lg ${
                editId
                  ? "bg-blue-50 text-blue-600"
                  : "bg-green-50 text-green-600"
              }`}
            >
              <IconUsers size={20} />
            </div>
            <Title order={4} className="text-gray-900 font-bold">
              {editId ? "Update Team Member" : "Add Team Member"}
            </Title>
          </Group>
        }
        opened={modalOpen}
        onClose={handleCloseModal}
        size="lg"
        radius="md"
        padding="lg"
        overlayBlur={2}
      >
        <div className="relative">
          <LoadingOverlay visible={formLoading} overlayBlur={2} />

          <div className="space-y-6">
            <div>
              <Text
                size="xs"
                color="dimmed"
                className="uppercase font-bold tracking-wider mb-2.5"
              >
                General Information
              </Text>
              <Grid gutter="md">
                <Grid.Col span={12} sm={6}>
                  <TextInput
                    error={formErrors.name}
                    required
                    label="Full Name"
                    placeholder="e.g. John Doe"
                    value={formState.name}
                    onChange={(e) =>
                      setFormState((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                  />
                </Grid.Col>
                <Grid.Col span={12} sm={6}>
                  <TextInput
                    error={formErrors.email}
                    required
                    label="Email Address"
                    placeholder="name@company.com"
                    type="email"
                    value={formState.email}
                    onChange={(e) =>
                      setFormState((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                  />
                </Grid.Col>

                {!editId && (
                  <Grid.Col span={12}>
                    <TextInput
                      required
                      error={formErrors.password}
                      placeholder="At least 5 characters"
                      label="Password"
                      type={showPassword ? "text" : "password"}
                      value={formState.password}
                      rightSection={
                        <ActionIcon
                          tabIndex={-1}
                          variant="subtle"
                          onClick={() => setShowPassword((prev) => !prev)}
                          aria-label={
                            showPassword ? "Hide password" : "Show password"
                          }
                        >
                          {showPassword ? (
                            <IconEyeOff size={16} stroke={1.5} />
                          ) : (
                            <IconEye size={16} stroke={1.5} />
                          )}
                        </ActionIcon>
                      }
                      onChange={(e) =>
                        setFormState((prev) => ({
                          ...prev,
                          password: e.target.value,
                        }))
                      }
                    />
                  </Grid.Col>
                )}
              </Grid>
            </div>

            <Divider my="sm" />

            <div>
              <Text
                size="xs"
                color="dimmed"
                className="uppercase font-bold tracking-wider mb-2.5"
              >
                Access & Permissions
              </Text>
              <Grid gutter="md">
                <Grid.Col span={12} sm={6}>
                  <MultiSelect
                    required
                    error={formErrors.hasAccessTo}
                    label="Allowed Module(s)"
                    placeholder="Select modules"
                    value={formState.hasAccessTo}
                    withinPortal
                    data={[
                      { label: "Agreement", value: "1" },
                      { label: "Recovery", value: "2" },
                      { label: "Obligation", value: "3" },
                    ]}
                    onChange={(val) => {
                      const activeModuleIds = val;
                      const updatedRoles = formState.userRole.filter(
                        (roleId) => {
                          const roleObj = userRoleData.find(
                            (r) => r.value === roleId,
                          );
                          return (
                            roleObj &&
                            activeModuleIds.includes(roleObj.moduleId)
                          );
                        },
                      );

                      setFormState((prev) => ({
                        ...prev,
                        hasAccessTo: val,
                        userRole: updatedRoles,
                      }));
                    }}
                  />
                </Grid.Col>

                <Grid.Col span={12} sm={6}>
                  <MultiSelect
                    error={formErrors.userRole}
                    required
                    label="Select Role(s)"
                    placeholder={
                      formState.hasAccessTo.length === 0
                        ? "Please select module(s) first"
                        : "Select roles"
                    }
                    value={formState.userRole}
                    withinPortal
                    disabled={formState.hasAccessTo.length === 0}
                    data={userRoleData.filter((role) =>
                      formState.hasAccessTo.includes(role.moduleId),
                    )}
                    onChange={(val) =>
                      setFormState((prev) => ({ ...prev, userRole: val }))
                    }
                  />
                </Grid.Col>

                <Grid.Col span={12} sm={6}>
                  <Select
                    required
                    error={formErrors.mapBusinessunit}
                    label="Business Unit"
                    placeholder="Choose unit"
                    value={formState.mapBusinessunit}
                    withinPortal
                    onChange={(val) =>
                      setFormState((prev) => ({
                        ...prev,
                        mapBusinessunit: val,
                      }))
                    }
                    data={buData}
                  />
                </Grid.Col>

                <Grid.Col span={12} sm={6}>
                  <Select
                    label="Status"
                    required
                    value={formState.enabled}
                    withinPortal
                    onChange={(val) =>
                      setFormState((prev) => ({ ...prev, enabled: val }))
                    }
                    data={[
                      { label: "Active / Enabled", value: "true" },
                      { label: "Inactive / Disabled", value: "false" },
                    ]}
                  />
                </Grid.Col>

                {!editId &&
                  formState.hasAccessTo.includes("3") &&
                  formState.userRole.some((id) =>
                    deptRequiredUserRoles.includes(Number(id)),
                  ) && (
                    <Grid.Col span={12}>
                      <Select
                        label="Department"
                        placeholder="Choose department"
                        value={formState.department}
                        error={formErrors.department}
                        withinPortal
                        data={[
                          { label: "FINANCE", value: "1" },
                          { label: "HR", value: "2" },
                          { label: "IT", value: "3" },
                          { label: "TAXATION", value: "4" },
                          { label: "MARKETING", value: "5" },
                          { label: "ACCESSORIES", value: "6" },
                          { label: "SPARES", value: "7" },
                          { label: "OPERATIONS", value: "8" },
                        ]}
                        onChange={(val) => {
                          setFormState((prev) => ({
                            ...prev,
                            department: val,
                          }));
                        }}
                      />
                    </Grid.Col>
                  )}
              </Grid>
            </div>

            <div className="flex justify-end gap-3 mt-8">
              <Button variant="subtle" color="gray" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button
                loading={formLoading}
                onClick={async () => {
                  if (!validate()) return;

                  setFormLoading(true);

                  const isEdit = editId > 0;
                  const apiFn = isEdit ? updateMasterUser : addMasterUser;
                  const actionLabel = isEdit ? "update" : "add";

                  const userDataPayload = {
                    name: formState.name,
                    email: formState.email,
                    phone: formState.phone,
                    password: formState.password,
                    userRole: formState.userRole.map(Number),
                    mapOrganization: Number(formState.mapOrganization),
                    mapBusinessunit: Number(formState.mapBusinessunit),
                    hasAccessTo: formState.hasAccessTo.map(Number),
                    enabled: formState.enabled === "true",
                    department: formState.department || "",
                  };

                  if (isEdit) {
                    userDataPayload.id = editId;
                  }

                  try {
                    const response = await apiWrapWithErrorWithData(
                      apiFn({ userData: userDataPayload }),
                    );

                    if (response.success) {
                      showNotification({
                        message: `User has been successfully ${actionLabel}d.`,
                        color: "green",
                        title: `User ${actionLabel}d`,
                      });
                      handleCloseModal();
                      await fetchUsers();
                    } else {
                      showNotification({
                        message: `Error: User could not be ${actionLabel}d.`,
                        color: "red",
                        title: `Error ${actionLabel}ing user`,
                      });
                    }
                  } catch (error) {
                    showNotification({
                      message: `An unexpected error occurred.`,
                      color: "red",
                      title: "Error",
                    });
                  } finally {
                    setFormLoading(false);
                  }
                }}
              >
                {editId ? "Update Member" : "Add Member"}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      <DeleteConfirmationModal
        title="Delete User"
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        deleteHandler={deleteUserHandler}
        deleteLoading={deleteLoading}
        text={
          userToDelete
            ? `Do you want to delete user "${userToDelete.name}"?`
            : "Do you want to delete this user?"
        }
      />
    </>
  );
};

export default TeamManager;
